/**
 * Avgifter per termin, hämtade direkt från School Harbor.
 *
 * Sidan visar en sektion per aktuell och kommande termin, med en snabbväljare
 * högst upp som scrollar till rätt sektion. Saknas prislistan visas inget alls
 * i stället för gamla eller gissade siffror.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { TERMS, dueTextFor, duePassed, registrationDueText, shortDueText } from "@/lib/fees";
import { sitePricingQuery } from "@/lib/pricing-query";
import type { ProgramId, SitePricing, SitePricingResult } from "@/lib/pricing-types";

type Locale = "sv" | "en" | "es";

type Row = { label: string; price: string; due: string; dueIsDate?: boolean };
type Group = { heading: string; rows: Row[] };



const TEXT: Record<
  Locale,
  {
    jumpLabel: string;
    term: string;
    year: string;
    shortWeek: string;
    shortMonth: string;
    perTerm: string;
    bookedPerTerm: string;
    registration: string;
    registrationShort: string;
    dueHeading: string;
    priceHeading: string;
    periodHeading: string;
    dueFallback: string;
    duePrefix: string;
    afterschool: string;
    programs: Record<ProgramId, string>;
    unavailable: string;
    siblingDiscountHeading: string;
    siblingDiscountRule: (childNumber: number, percent: number) => string;
  }
> = {
  sv: {
    jumpLabel: "Hoppa till termin",
    term: "Terminsavgift",
    year: "Helår",
    shortWeek: "Korttid 1 vecka (max 2 veckor)",
    shortMonth: "Korttid 1 månad (max 2 månader)",
    perTerm: "per termin",
    bookedPerTerm: "Bokas en termin i taget",
    registration: "Inskrivningsavgift per elev (engångsbetalning)",
    registrationShort: "Inskrivningsavgift korttidsregistrering",
    dueHeading: "Betalas senast",
    priceHeading: "Pris",
    periodHeading: "Period",
    dueFallback: "Enligt betalningsplanen vid antagning",
    duePrefix: "Betalas senast",
    afterschool: "Fritids",
    programs: {
      primary: "Grundskola årskurs 1–6",
      preschool_class: "Förskoleklass (5–6 år)",
      preschool: "Förskola (3–5 år)",
      sofia: "Sofia Distans årskurs 7–9 (handledning)",
      hermods: "Hermods gymnasium (handledning)",
    },
    unavailable: "Prislistan är tillfälligt otillgänglig. Kontakta skolan för aktuella avgifter.",
    siblingDiscountHeading: "Flerbarnsrabatt",
    siblingDiscountRule: (childNumber, percent) => `${percent} % rabatt från familjens elev ${childNumber}, på den lägsta skolavgiften.`,
  },
  en: {
    jumpLabel: "Jump to term",
    term: "Term fee",
    year: "Full year",
    shortWeek: "Short term 1 week (max 2 weeks)",
    shortMonth: "Short term 1 month (max 2 months)",
    perTerm: "per term",
    bookedPerTerm: "Booked one term at a time",
    registration: "Registration fee per student (one-off)",
    registrationShort: "Registration fee, short-term enrolment",
    dueHeading: "Due date",
    priceHeading: "Price",
    periodHeading: "Period",
    dueFallback: "As per the payment plan agreed at admission",
    duePrefix: "Due",
    afterschool: "After-school club",
    programs: {
      primary: "Primary school Years 1–6",
      preschool_class: "Reception year (5–6 years)",
      preschool: "Preschool (3–5 years)",
      sofia: "Sofia Distans Years 7–9 (tutoring)",
      hermods: "Hermods upper secondary (tutoring)",
    },
    unavailable: "The fee list is temporarily unavailable. Please contact the school for current fees.",
    siblingDiscountHeading: "Sibling discount",
    siblingDiscountRule: (childNumber, percent) => `${percent}% discount from student ${childNumber} in the family, applied to the lowest school fee.`,
  },
  es: {
    jumpLabel: "Ir al periodo",
    term: "Tarifa por periodo",
    year: "Curso completo",
    shortWeek: "Estancia corta 1 semana (máx. 2 semanas)",
    shortMonth: "Estancia corta 1 mes (máx. 2 meses)",
    perTerm: "por periodo",
    bookedPerTerm: "Se reserva por periodos",
    registration: "Matrícula por alumno (pago único)",
    registrationShort: "Matrícula para estancia corta",
    dueHeading: "Fecha límite",
    priceHeading: "Precio",
    periodHeading: "Periodo",
    dueFallback: "Según el plan de pago acordado en la admisión",
    duePrefix: "Pago antes de",
    afterschool: "Actividades extraescolares",
    programs: {
      primary: "Primaria, cursos 1–6",
      preschool_class: "Último año de infantil (5–6 años)",
      preschool: "Preescolar (3–5 años)",
      sofia: "Sofia Distans, cursos 7–9 (tutoría)",
      hermods: "Bachillerato Hermods (tutoría)",
    },
    unavailable: "La lista de tarifas no está disponible temporalmente. Contacte con el colegio para conocer las tarifas actuales.",
    siblingDiscountHeading: "Descuento por hermanos",
    siblingDiscountRule: (childNumber, percent) => `${percent} % de descuento desde el alumno ${childNumber} de la familia, sobre la cuota escolar más baja.`,
  },
};




const TERM_LABELS: Record<string, Record<Locale, string>> = {
  HT26: { sv: "Hösttermin 2026", en: "Autumn term 2026", es: "Otoño 2026" },
  VT27: { sv: "Vårtermin 2027", en: "Spring term 2027", es: "Primavera 2027" },
  HT27: { sv: "Hösttermin 2027", en: "Autumn term 2027", es: "Otoño 2027" },
  VT28: { sv: "Vårtermin 2028", en: "Spring term 2028", es: "Primavera 2028" },
  HT28: { sv: "Hösttermin 2028", en: "Autumn term 2028", es: "Otoño 2028" },
  VT29: { sv: "Vårtermin 2029", en: "Spring term 2029", es: "Primavera 2029" },
};

function formatAmount(amount: number, locale: Locale): string {
  const n = new Intl.NumberFormat(locale === "sv" ? "sv-SE" : locale === "es" ? "es-ES" : "en-GB").format(amount);
  return locale === "es" ? `${n} euros` : `${n} Euro`;
}

function termLabel(code: string, locale: Locale, fallback: string | undefined): string {
  const known = TERM_LABELS[code]?.[locale];
  if (known) return known;
  const fromTerms = TERMS.find((t) => t.id === code)?.label;
  return fromTerms ?? fallback ?? code;
}

/** Terminer med priser som inte redan är avslutade, i kronologisk ordning. */
export function upcomingTerms(
  pricing: SitePricing,
  visibleSetting: string[] | undefined,
): { code: string; name: string }[] {
  const today = new Date().toISOString().slice(0, 10);
  const visible = visibleSetting?.length ? visibleSetting : pricing.termCodes;
  return pricing.termCodes
    .filter((code) => visible.includes(code))
    .map((code) => pricing.terms.find((t) => t.code === code) ?? { code, name: code, startDate: "", endDate: "" })
    .filter((t) => !t.endDate || t.endDate >= today)
    .map((t) => ({ code: t.code, name: t.name }));
}

function buildGroups(pricing: SitePricing, code: string, locale: Locale): Group[] {
  const t = TEXT[locale];
  // Alla förfallotexter byggs från förfalloreglerna i School Harbor.
  const dueText = dueTextFor(code, locale);
  const due = dueText ?? t.dueFallback;
  const dueShort = shortDueText(locale, code) ?? t.dueFallback;
  // Datumrader får prefixet "Betalas senast"; regeltexter skrivs som de är.
  const dueIsDate = dueText !== null && !duePassed(code);
  const regDue = registrationDueText(code, locale, "fulltime") ?? t.dueFallback;
  const regShortDue = registrationDueText(code, locale, "shortterm") ?? t.dueFallback;
  const groups: Group[] = [];

  for (const program of pricing.programs) {
    const prices = program.byTerm[code];
    if (!prices) continue;
    const rows: Row[] = [];
    if (prices.term != null) rows.push({ label: t.term, price: formatAmount(prices.term, locale), due });
    if (prices.year != null) rows.push({ label: t.year, price: formatAmount(prices.year, locale), due });
    if (prices.shortWeek != null)
      rows.push({ label: t.shortWeek, price: formatAmount(prices.shortWeek, locale), due: dueShort });
    if (prices.shortMonth != null)
      rows.push({ label: t.shortMonth, price: formatAmount(prices.shortMonth, locale), due: dueShort });
    if (rows.length) groups.push({ heading: t.programs[program.id], rows });
  }

  const after = pricing.afterschool.byTerm[code];
  if (after) {
    const rows: Row[] = [];
    if (after.term != null) rows.push({ label: t.term, price: formatAmount(after.term, locale), due });
    if (after.year != null) rows.push({ label: t.year, price: formatAmount(after.year, locale), due });
    else rows.push({ label: t.year, price: t.bookedPerTerm, due });
    if (after.shortWeek != null)
      rows.push({ label: t.shortWeek, price: formatAmount(after.shortWeek, locale), due: dueShort });
    if (after.shortMonth != null)
      rows.push({ label: t.shortMonth, price: formatAmount(after.shortMonth, locale), due: dueShort });
    if (rows.some((r) => r.price !== t.bookedPerTerm)) groups.push({ heading: t.afterschool, rows });
  }

  for (const activity of pricing.activities) {
    const setting = activity.byTerm[code];
    if (!setting?.active || setting.fee == null) continue;
    groups.push({
      heading: activity.heading,
      rows: [
        { label: t.term, price: formatAmount(setting.fee, locale), due },
        { label: t.year, price: t.bookedPerTerm, due },
      ],
    });
  }

  const reg = pricing.registrationFee[code];
  if (reg && (reg.full != null || reg.short != null)) {
    const rows: Row[] = [];
    if (reg.full != null) rows.push({ label: t.registration, price: formatAmount(reg.full, locale), due: regDue });
    if (reg.short != null)
      rows.push({ label: t.registrationShort, price: formatAmount(reg.short, locale), due: regShortDue });
    groups.push({ heading: t.registration.split("(")[0]!.trim(), rows });
  }

  return groups.map((g) => ({
    ...g,
    rows: g.rows.map((r) => ({ ...r, dueIsDate: r.due === due ? dueIsDate : false })),
  }));
}

function GroupTable({ group, text }: { group: Group; text: (typeof TEXT)["sv"] }) {
  return (
    <div>
      <h4 className="text-base font-semibold">{group.heading}</h4>
      <ul className="mt-3 space-y-3 sm:hidden">
        {group.rows.map((r) => (
          <li key={r.label} className="rounded-xl border border-border bg-card p-4 shadow-soft">
            <p className="text-sm font-medium">{r.label}</p>
            <p className="mt-1 text-lg font-semibold text-primary">{r.price}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {r.dueIsDate === false ? r.due : `${text.duePrefix} ${r.due.toLowerCase()}`}
            </p>
          </li>
        ))}
      </ul>
      <div className="mt-3 hidden rounded-xl border border-border bg-card shadow-soft sm:block">
        <table className="w-full table-fixed text-left text-sm">
          <colgroup>
            <col className="w-[52%]" />
            <col className="w-[24%]" />
            <col className="w-[24%]" />
          </colgroup>
          <thead className="bg-sand text-xs tracking-[0.12em] text-muted-foreground uppercase">
            <tr>
              <th scope="col" className="px-5 py-3 font-medium">
                {text.periodHeading}
              </th>
              <th scope="col" className="px-5 py-3 font-medium">
                {text.priceHeading}
              </th>
              <th scope="col" className="px-5 py-3 font-medium">
                {text.dueHeading}
              </th>
            </tr>
          </thead>
          <tbody>
            {group.rows.map((r) => (
              <tr key={r.label} className="border-t border-border">
                <td className="px-5 py-3">{r.label}</td>
                <td className="px-5 py-3 font-medium">{r.price}</td>
                <td className="px-5 py-3 text-muted-foreground">{r.due}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FeesByTerm({ locale, initialData, embedded = false }: { locale: Locale; initialData?: SitePricingResult; embedded?: boolean }) {
  const text = TEXT[locale];
  const { data } = useQuery(sitePricingQuery);
  const resolvedData = initialData ?? data;
  const pricing = resolvedData?.pricing ?? null;
  const [active, setActive] = useState<string | null>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const rootRef = useRef<HTMLDivElement | null>(null);
  const embeddedScrollRef = useRef<HTMLDivElement | null>(null);

  const terms = useMemo(
    () => (pricing ? upcomingTerms(pricing, resolvedData?.termSettings?.visible) : []),
    [pricing, resolvedData?.termSettings?.visible],
  );

  // Scrollspy som fungerar åt båda hållen: vi jämför varje sektions position
  // mot en linje strax under den fastnaglade väljaren och tar den sista
  // sektion som passerat linjen. Backar man uppåt hoppar markeringen tillbaka.
  useEffect(() => {
    if (terms.length === 0) return;
    setActive((prev) => prev ?? terms[0]!.code);
    let frame = 0;
    const scrollRoot = embedded ? embeddedScrollRef.current : null;
    const update = () => {
      frame = 0;
      const rootTop = scrollRoot?.getBoundingClientRect().top ?? 0;
      const line = rootTop + (embedded ? 72 : 160);
      let current = terms[0]!.code;
      for (const term of terms) {
        const el = sectionRefs.current[term.code];
        if (!el) continue;
        if (el.getBoundingClientRect().top <= line) current = term.code;
      }
      // Längst ner på sidan markeras alltid sista terminen.
      const reachedBottom = scrollRoot
        ? scrollRoot.scrollTop + scrollRoot.clientHeight >= scrollRoot.scrollHeight - 4
        : window.innerHeight + window.scrollY >= document.body.scrollHeight - 4;
      if (reachedBottom) {
        current = terms[terms.length - 1]!.code;
      }
      setActive(current);
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    update();
    const target: Window | HTMLElement = scrollRoot ?? window;
    target.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      target.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [terms, embedded]);

  // Håll den aktiva knappen synlig i den vågräta raden på mobil.
  useEffect(() => {
    if (!active) return;
    buttonRefs.current[active]?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [active]);


  if (!pricing || terms.length === 0) {
    return <p className="mt-6 rounded-md border border-border bg-muted/40 p-4 text-sm text-muted-foreground">{text.unavailable}</p>;
  }

  return (
    <div ref={rootRef} className={embedded ? "flex h-full min-h-0 flex-col" : "mt-6"}>
      <nav
        aria-label={text.jumpLabel}
        className={embedded
          ? "shrink-0 border-b border-border bg-background px-5 py-3"
          : "sticky top-[4.75rem] z-10 -mx-5 bg-background/90 px-5 py-3 backdrop-blur min-[800px]:top-20"}
      >
        <div className="flex items-center gap-3 overflow-x-auto pb-1">
          <p className="shrink-0 text-xs tracking-[0.12em] text-muted-foreground uppercase">{text.jumpLabel}</p>
          {terms.map((term) => (
            <button
              key={term.code}
              type="button"
              ref={(el) => {
                buttonRefs.current[term.code] = el;
              }}
              onClick={() => {
                const section = sectionRefs.current[term.code];
                const scrollRoot = embedded ? embeddedScrollRef.current : null;
                if (!section) return;
                if (scrollRoot) {
                  const top = section.getBoundingClientRect().top - scrollRoot.getBoundingClientRect().top + scrollRoot.scrollTop - 72;
                  scrollRoot.scrollTo({ top, behavior: "smooth" });
                } else {
                  section.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }}
              className={`shrink-0 rounded-full border px-4 py-1.5 text-sm transition-colors ${
                active === term.code
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-foreground/80 hover:border-primary/50"
              }`}
            >
              {term.code}
            </button>
          ))}
        </div>
      </nav>

      <div
        ref={embedded ? embeddedScrollRef : undefined}
        {...(embedded ? { "data-page-modal-scroll": true, "data-modal-scroll": true } : {})}
        className={embedded ? "min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-8 [scrollbar-color:var(--border)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]" : ""}
      >
      {embedded && pricing.siblingDiscounts.some((rule) => rule.percent > 0) ? (
        <section className="mt-6 rounded-md border border-border bg-muted/40 p-4">
          <h3 className="text-base font-semibold">{text.siblingDiscountHeading}</h3>
          <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
            {pricing.siblingDiscounts.filter((rule) => rule.percent > 0).map((rule) => (
              <li key={rule.childNumber}>{text.siblingDiscountRule(rule.childNumber, rule.percent)}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="mt-8 space-y-14">
        {terms.map((term) => {
          const groups = buildGroups(pricing, term.code, locale);
          if (groups.length === 0) return null;
          return (
            <div
              key={term.code}
              data-term={term.code}
              id={`termin-${term.code.toLowerCase()}`}
              ref={(el: HTMLDivElement | null) => {
                sectionRefs.current[term.code] = el;
              }}
              className={embedded ? "scroll-mt-20" : "scroll-mt-40 min-[800px]:scroll-mt-44"}
            >
              <h3 className="text-xl font-semibold">
                {termLabel(term.code, locale, term.name)}{" "}
                <span className="text-muted-foreground">({term.code})</span>
              </h3>
              <div className="mt-6 space-y-8">
                {groups.map((g) => (
                  <GroupTable key={g.heading} group={g} text={text} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}
